import { useContext, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItemText,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    Box,
    CircularProgress,
    TextField,
    InputAdornment,
    Typography,
} from '@mui/material';

import {
    Search as SearchIcon,
    DevicesOther as DeviceIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../AppContext';
import { SttList } from './SttList';

type Item = {
    id: number;
    name: string;
};

type ItemsResponse = {
    rows?: Item[];
};

type Props = {
    title: string;
    items: Item[];
    onAdd: (itemId: number) => void;
    onDelete: (itemId: number) => void;
    queryKey: string;
    itemQueryCall: (params: unknown) => Promise<ItemsResponse>;
    queryCallParameters?: unknown;
};

/*
 * Generalización del componente SttTerrainList
 */

export function SttItemList({
    title,
    items,
    onAdd,
    onDelete,
    queryKey,
    itemQueryCall,
    queryCallParameters,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [selectionOpen, setSelectionOpen] = useState(false);
    const [optionalItems, setOptionalItems] = useState<Item[] | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const getItemsQuery = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            itemQueryCall(queryCallParameters)
                .then((response: any) => {
                    const filteredItems = response?.rows?.filter(
                        (optionalItem: any) =>
                            !items?.find((i: any) => i.id === optionalItem.id),
                    );
                    setOptionalItems(filteredItems || []);
                    return response;
                })
                .catch((error: { msg?: string }) => {
                    cxt?.showMessage('E', t(error?.msg || ''));
                    throw error;
                }),
        enabled: true,
    });

    // Filter items based on search term
    const filteredItems = useMemo(() => {
        if (!optionalItems) return [];
        if (!searchTerm.trim()) return optionalItems;
        return optionalItems.filter((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [optionalItems, searchTerm]);

    function openAddModal() {
        setSearchTerm('');
        setSelectionOpen(true);
    }

    function selectItem(item: Item) {
        setSelectionOpen(false);
        setOptionalItems(
            optionalItems?.filter(
                (optionalItem) => optionalItem?.id !== item?.id,
            ) || null,
        );
        onAdd(item?.id);
    }

    if (getItemsQuery?.isLoading) {
        return (
            <Box display="flex" justifyContent="center" paddingY={15}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <SttList
                title={title}
                onAdd={() => openAddModal()}
                onDelete={(item: Item) => onDelete(item?.id)}
                data={
                    items &&
                    items.map((item: any) => ({
                        caption: `${item?.name}`,
                        objToSelect: item,
                    }))
                }
            />
            <Dialog
                onClose={() => setSelectionOpen(false)}
                open={selectionOpen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t(title)}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder={t('Search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <List
                        sx={{
                            maxHeight: 400,
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {filteredItems.length === 0 ? (
                            <Box py={4} textAlign="center">
                                <Typography color="text.secondary">
                                    {searchTerm
                                        ? t('NoResultsFound')
                                        : t('NoDevicesAvailable')}
                                </Typography>
                            </Box>
                        ) : (
                            filteredItems.map((item: Item) => (
                                <ListItemButton
                                    key={item.id}
                                    onClick={() => selectItem(item)}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': {
                                            borderBottom: 'none',
                                        },
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.light',
                                                width: 36,
                                                height: 36,
                                            }}
                                        >
                                            <DeviceIcon
                                                sx={{
                                                    fontSize: 20,
                                                    color: 'var(--brand-text)',
                                                }}
                                            />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={item.name}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                        }}
                                    />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
}
